import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { BiX } from 'react-icons/bi';
import { ValidationError } from 'yup';
import { FormHandles } from '@unform/core';
import { IoIosWarning } from 'react-icons/io';

import Tab from '../../../../newComponents/Tab';
import TabBar from '../../../../newComponents/TabBar';
import Button from '../../../../newComponents/Button';
import { ModalHandles } from '../../../../newComponents/Modal';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { usePizzaProduct, Image } from '../../../../hooks/pizzaProduct';

import Details, { DetailsFormSchema } from './steps/Details';
import Sizes, { SizesFormSchema } from './steps/Sizes';
import Images, { ImagesFormSchema } from './steps/Images';
import Complements, { ComplementsFormSchema } from './steps/Complements';
import Availability, { AvailabilityFormSchema } from './steps/Availability';

import getValidationErrors from '../../../../utils/getValidationErrors';

import ProductsService from '../../../../services/ProductsService';
import { Product } from '../../../../services/GroupsService';

import APIError from '../../../../errors/APIError';

import { Container, TabPanel, Modal, ModalHeader } from './styles';
import SizesService from '../../../../services/SizesService';

type PizzaProductProps = {
  lastProductIndex: number;
  onSuccessSave(groupId: number, product: Product): void;
};

const LAST_TAB_INDEX = 4;

const SCHEMAS = {
  0: DetailsFormSchema,
  1: SizesFormSchema,
  2: ImagesFormSchema,
  3: ComplementsFormSchema,
  4: AvailabilityFormSchema,
};

const PizzaProduct: React.FC<PizzaProductProps> = ({
  lastProductIndex,
  onSuccessSave,
}) => {
  const formRef = useRef<FormHandles>(null);
  const modalRef = useRef<ModalHandles>(null);
  const formMainRef = useRef<HTMLDivElement>(null);

  const {
    id,
    name,
    gtin,
    observation,
    grids,
    image,
    images,
    details,
    groupId,
    display,
    isCloning,
    currentTab,
    selectedGrid,
    numOfFlavors,
    complements,
    groupsOptions,
    isDrawerOpened,
    unitOfMeasurementId,
    isAwaysAvailability,
    unitsOfMeasurementOptions,
    isComplementsDrawerOpened,
    handleChangeTab,
    handleChangeSizes,
    handleCloseDrawer,
    handleResetProduct,
    handleChangeProduct,
  } = usePizzaProduct();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = useCallback(() => {
    modalRef.current?.close();
    handleCloseDrawer();

    const mainImage = document.getElementById(
      'firstPizzaProductImage',
    ) as HTMLInputElement;
    const img0 = document.getElementById(
      'pizzaProductImage-0',
    ) as HTMLInputElement;
    const img1 = document.getElementById(
      'pizzaProductImage-1',
    ) as HTMLInputElement;
    const img2 = document.getElementById(
      'pizzaProductImage-2',
    ) as HTMLInputElement;
    const img3 = document.getElementById(
      'pizzaProductImage-3',
    ) as HTMLInputElement;
    const img4 = document.getElementById(
      'pizzaProductImage-4',
    ) as HTMLInputElement;
    const img5 = document.getElementById(
      'pizzaProductImage-5',
    ) as HTMLInputElement;

    mainImage.value = '';
    img0.value = '';
    img1.value = '';
    img2.value = '';
    img3.value = '';
    img4.value = '';
    img5.value = '';

    setTimeout(() => {
      handleChangeTab(0);
      handleResetProduct();
      formRef.current?.reset();
      formRef.current?.setErrors({});
    }, 500);
  }, [handleCloseDrawer, handleChangeTab, handleResetProduct]);

  const handleTryCancel = useCallback(() => {
    modalRef.current?.open();
  }, []);

  const handleLoadSizes = useCallback(async () => {
    try {
      const sizesList = await SizesService.listSizes(user.empresa_id, 3);

      handleChangeSizes(
        sizesList.map((size) => ({
          id: size.id,
          name: size.name,
          order: size.order,
          numberOfFlavors: size.numOfFlavors,
        })),
      );
      if (!id && (!isCloning || display)) {
        handleChangeProduct({
          grids: sizesList.map((size) => ({
            id: String(Math.random()),
            active: true,
            currentStock: 0,
            minimumStock: 0,
            discount: 0,
            name: size.name,
            order: size.order,
            price: 0,
            sizeId: size.id,
            image: undefined,
            code: '',
            numberOfFlavors: size.numOfFlavors,
            numberOfSlices: size.numOfSlices,
            canChangeCurrentStock: false,
          })),
        });
      }
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    }
  }, [
    id,
    display,
    isCloning,
    user.empresa_id,
    addToast,
    handleChangeSizes,
    handleChangeProduct,
  ]);

  useEffect(() => {
    if (isDrawerOpened) {
      handleLoadSizes();
    }
  }, [isDrawerOpened, handleLoadSizes]);

  useEffect(() => {
    const defaultGroup = groupsOptions.find((group) => group.value === groupId);
    const defaultUnitOfMeasurement = unitsOfMeasurementOptions.find(
      (unitOfMeasurement) => unitOfMeasurement.value === unitOfMeasurementId,
    );

    formRef.current?.setData({
      name,
      gtin,
      observation,
      group: defaultGroup,
      unitOfMeasurement: defaultUnitOfMeasurement,
    });
  }, [
    name,
    gtin,
    observation,
    formRef,
    groupId,
    groupsOptions,
    unitOfMeasurementId,
    unitsOfMeasurementOptions,
  ]);

  useEffect(() => {
    if (!isDrawerOpened) {
      if (formMainRef.current) {
        formMainRef.current.scrollTop = 0;
      }
    }
  }, [isDrawerOpened]);

  const handleSubmit = useCallback(
    async (data) => {
      if (isComplementsDrawerOpened) return;

      setIsLoading(true);

      formRef.current?.setErrors({});

      try {
        const currentTabSchema = SCHEMAS[currentTab];

        const formValues = await currentTabSchema.validate(data, {
          abortEarly: false,
        });

        if (display) {
          if (!selectedGrid || !numOfFlavors) {
            throw new APIError(`Escolha um tamanho e a quantidade de sabores`);
          }
        } else {
          await Promise.all(
            ((formValues as any)?.grids as Array<any>).map(
              async (grid: any, index) => {
                if (grid.code) {
                  const data2 = {
                    code: grid.code,
                  };

                  if (typeof grids[index].id === 'number') {
                    Object.assign(data2, {
                      productPizzaId: grids[index].id,
                    });
                  }

                  await ProductsService.validatePDVCode(
                    user.empresa_id,
                    data2,
                  ).catch((err) => {
                    throw new APIError(
                      `Código já em uso-${`grids[${index}].code-${err.message}`}`,
                    );
                  });
                }
              },
            ),
          );

          ((formValues as any)?.grids as Array<any>).map((grid, gridIndex) => {
            grids.forEach((_, index) => {
              if (
                formRef.current?.getFieldValue(`grids[${index}].code`) &&
                formRef.current?.getFieldValue(`grids[${index}].code`) ===
                  grid.code &&
                index !== gridIndex
              ) {
                throw new APIError(
                  `Você já escolheu este código-${`grids[${index}].code`}`,
                );
              }
            });
          });

          (formValues as any)?.grids.forEach((grid: any, index: number) => {
            if (grids[index].active && grid.price === 0) {
              const validationError = new ValidationError(
                ['Preço inválido'],
                grid.price,
                `grid[${index}].price`,
              );

              validationError.inner = [
                new ValidationError(
                  ['Preço inválido'],
                  grid.price,
                  `grids[${index}].price`,
                  'min',
                ),
              ];

              throw validationError;
            }
          });
        }

        if (currentTab === LAST_TAB_INDEX || id) {
          await Promise.all(
            Object.values(SCHEMAS).map(async (schema, index) => {
              if (index === 1 && display) return;

              await schema.validate(data, {
                abortEarly: false,
              });
            }),
          );

          const invalidShiftExists = details.shifts.find(
            (shift) => shift.startTime > shift.endTime,
          );

          if (!isAwaysAvailability && invalidShiftExists) {
            handleChangeTab(4);

            addToast({
              title: 'Turno inválido',
              description: 'O início do turno deve ser maior do que o fim',
              type: 'error',
            });

            return;
          }

          const formData = new FormData();

          const imagesToUpload: Image[] = [];

          if (image instanceof File) {
            formData.append('image', image.name);
            formData.append('files', image);
          }

          images.forEach((imageToUpload) => {
            if (imageToUpload) {
              if (imageToUpload.file instanceof File) {
                formData.append('files', imageToUpload.file);
                imagesToUpload.push({
                  ...imageToUpload,
                  file: imageToUpload.file.name,
                });
              } else {
                imagesToUpload.push(imageToUpload);
              }
            }
          });

          const parsedComplements = (complements || []).map((complement) => ({
            ...complement,
            id: typeof complement.id === 'number' ? complement.id : undefined,
            items: (complement.items || []).map((item) => {
              const commomItem = {
                ...item,
                id: typeof item.id === 'number' ? item.id : undefined,
                image: undefined,
                price: {
                  value: item.price,
                },
                rawMaterialId:
                  typeof item.rawMaterialId === 'number'
                    ? item.rawMaterialId || undefined
                    : undefined,
              };

              if (item?.image instanceof File) {
                formData.append('files', item.image);

                return {
                  ...commomItem,
                  image: item.image.name,
                };
              }

              return commomItem;
            }),
          }));

          if (!display) {
            const parsedGrids = grids
              .sort((a, b) => a.order - b.order)
              .map((grid, gridIndex) => ({
                ...grid,
                ...data.grids[gridIndex],
                minimumStock: Number(data.grids[gridIndex].minimumStock || 0),
                currentStock:
                  Number(data.grids[gridIndex].currentStock || 0) +
                  Number(data.grids[gridIndex].addStock || 0),
                id: typeof grid.id === 'number' ? grid.id : undefined,
                price: {
                  value: Number(data.grids[gridIndex].price || 0),
                },
                discount: {
                  price:
                    Number(data.grids[gridIndex].price || 0) *
                    (Number(data.grids[gridIndex].discount || 0) / 100),
                  active: Number(data.grids[gridIndex].discount || 0) / 100 > 0,
                  value: Number(data.grids[gridIndex].discount || 0) / 100,
                },
              }));

            formData.append('grids', JSON.stringify(parsedGrids));
          } else {
            let displayGrid = {};

            if (id) {
              displayGrid = {
                ...grids[0],
                sizeId: selectedGrid,
                numberOfFlavors: numOfFlavors,
                minimumStock: 0,
                currentStock: 0,
                id: typeof grids[0]?.id === 'number' ? grids[0]?.id : undefined,
                price: {
                  value: 0,
                },
                discount: {
                  price: 0,
                  active: 0,
                  value: 0,
                },
              };
            } else {
              const selectedGridItem = grids.find(
                (grid) => grid.sizeId === selectedGrid,
              );

              displayGrid = {
                ...selectedGridItem,
                numberOfFlavors: numOfFlavors,
                minimumStock: 0,
                currentStock: 0,
                id:
                  typeof selectedGridItem?.id === 'number'
                    ? selectedGridItem?.id
                    : undefined,
                price: {
                  value: 0,
                },
                discount: {
                  price: 0,
                  active: 0,
                  value: 0,
                },
              };
            }

            formData.append('grids', JSON.stringify([displayGrid]));
          }

          const parsedDetails = {
            dias: {
              sexta: isAwaysAvailability || details.days.friday,
              terca: isAwaysAvailability || details.days.tuesday,
              quarta: isAwaysAvailability || details.days.wednesday,
              quinta: isAwaysAvailability || details.days.thursday,
              sabado: isAwaysAvailability || details.days.saturday,
              domingo: isAwaysAvailability || details.days.sunday,
              segunda: isAwaysAvailability || details.days.monday,
            },
            turnos: isAwaysAvailability
              ? []
              : details.shifts.map((shift) => ({
                  inicio: shift.startTime,
                  fim: shift.endTime,
                })),
          };

          formData.append('type', '1');
          formData.append('controlGrid', '2');
          formData.append('name', data.name);
          formData.append('groupId', data.group);
          formData.append('gtin', data.gtin);
          formData.append('display', String(Boolean(display)));
          formData.append('images', JSON.stringify(imagesToUpload));
          formData.append('observation', data.observation);
          formData.append('availability', JSON.stringify(parsedDetails));
          formData.append('complements', JSON.stringify(parsedComplements));
          formData.append('unitOfMeasurementId', data.unitOfMeasurement);

          let product;

          if (id) {
            product = await ProductsService.editProduct(
              id,
              user.empresa_id,
              data.group,
              formData,
            );
          } else {
            formData.append('order', String(lastProductIndex + 1));
            product = await ProductsService.createProduct(
              user.empresa_id,
              data.group,
              formData,
            );
          }

          let unitOfMeasurementAcronym = '';

          const unitOfMeasurement =
            unitsOfMeasurementOptions.find(
              (foundUnitOfMeasurement) =>
                foundUnitOfMeasurement.value === data.unitOfMeasurement,
            )?.label || '';

          const unitsOfMeasurementMatches = unitOfMeasurement.match(
            /(\(.*?\))|<\w+(\s+("[^"]*"|'[^']*'|[^>])+)?>|<\/\w+>/gi,
          );

          if (unitsOfMeasurementMatches && unitsOfMeasurementMatches.length) {
            unitOfMeasurementAcronym = unitsOfMeasurementMatches[0]
              .replace('(', '')
              .replace(')', '');
          }

          onSuccessSave(data.group, {
            id: product.id,
            name: product.name,
            stock: undefined,
            order: Number(product.order),
            code: product.code,
            unitOfMeasurement: {
              acronym: unitOfMeasurementAcronym,
            },
            image: product.image,
            price: undefined,
            pauseSale: Boolean(product.pauseSale),
            numOfGrids: product?.numOfGrids || 0,
            minGridsPrice: product?.minGridsPrice || 0,
            grids: product?.grids || [],
          });

          handleCancel();

          return;
        }

        handleChangeTab(currentTab + 1);
      } catch (error) {
        console.log(error);
        let errors = {};

        if (error instanceof ValidationError) {
          errors = getValidationErrors(error);

          const detailsTabFields = [
            'group',
            'name',
            'gtin',
            'observation',
            'unitOfMeasurement',
          ];

          if (
            Object.keys(errors).some((field) =>
              detailsTabFields.includes(field),
            )
          ) {
            handleChangeTab(0);
          }

          if (Object.keys(errors).some((field) => !!field.match(/grids/))) {
            handleChangeTab(1);
          }

          formRef.current?.setErrors(errors);
        }

        if (error instanceof APIError) {
          if (error.message.includes('Código já em uso')) {
            handleChangeTab(1);

            formRef.current?.setFieldError(
              error.message.split('-')[1],
              'Código já em uso!',
            );
            addToast({
              title: error.message.split('-')[2],
              type: 'error',
            });
          } else if (error.message.includes('Você já escolheu este código')) {
            formRef.current?.setFieldError(
              error.message.split('-')[1],
              'Você já escolheu este código!',
            );
          } else {
            addToast({
              title: error.message,
              type: 'error',
            });
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      image,
      grids,
      display,
      selectedGrid,
      numOfFlavors,
      images,
      details,
      currentTab,
      complements,
      isAwaysAvailability,
      isComplementsDrawerOpened,
      unitsOfMeasurementOptions,
      user.empresa_id,
      lastProductIndex,
      addToast,
      handleCancel,
      onSuccessSave,
      handleChangeTab,
    ],
  );

  return (
    <Container isVisible={isDrawerOpened}>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={{ grids }}>
        <header>
          <div className="drawer-title">
            <h1>{id ? 'Editar' : 'Novo'} produto</h1>
            <button
              type="button"
              onClick={handleTryCancel}
              disabled={isLoading}
            >
              <BiX />
            </button>
          </div>
          <TabBar
            selectedTab={currentTab}
            onChange={(_event, value) => handleChangeTab(value)}
          >
            <Tab wrapped style={{ fontSize: '14px' }}>
              Detalhes
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Tamanhos
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Imagens
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Complementos
            </Tab>
            <Tab wrapped style={{ fontSize: '14px' }}>
              Disponibilidade
            </Tab>
          </TabBar>
        </header>
        <main ref={formMainRef} style={{ height: '100%' }}>
          <TabPanel index={0} selectedTab={currentTab}>
            <Details />
          </TabPanel>
          <TabPanel
            index={1}
            selectedTab={currentTab}
            style={{ height: '100%' }}
          >
            <Sizes formRef={formRef} />
          </TabPanel>
          <TabPanel index={2} selectedTab={currentTab}>
            <Images />
          </TabPanel>
          <TabPanel index={3} selectedTab={currentTab}>
            <Complements />
          </TabPanel>
          <TabPanel index={4} selectedTab={currentTab}>
            <Availability />
          </TabPanel>
        </main>
        <footer>
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={handleTryCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            {currentTab === LAST_TAB_INDEX || id ? 'Salvar' : 'Continuar'}
          </Button>
        </footer>
      </Form>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você não salvou suas alterações. Ao realizar a ação todos os dados
          serão perdidos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleCancel}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default PizzaProduct;
